import { Box } from '@mui/material';
import { useMemo } from 'react';
import {
  BooleanField,
  ReferenceManyField,
  TextField,
  useListContext,
  useRecordContext,
} from 'react-admin';

import {
  GetB2bAdminsQuery,
  GetB2bAdminsResponse,
  getB2bAdminsSortColumns,
} from '@Common/types/bo/b2b/admins';
import { GetB2bCompaniesOneResponse } from '@Common/types/bo/b2b/companies';

import {
  adminRoleChoices,
  adminStatusChoices,
  languagePreferenceChoices,
} from '@sh4res/bo-common/choices/admins';
import { B2bAdminCreate } from '@sh4res/bo-common/components/B2bAdmins/B2bAdminCreate';
import { B2bAdminSendInvitesButton } from '@sh4res/bo-common/components/B2bAdmins/B2bAdminSendInvitesButton';
import { canResendB2bAdminInvitation } from '@sh4res/bo-common/components/B2bAdmins/canResendB2bAdminInvitation';
import { genderChoices } from '@sh4res/bo-common/components/B2bEmployee/choices';
import { DatagridTyped } from '@sh4res/bo-common/components/DatagridTyped';
import { EmptyScreen } from '@sh4res/bo-common/components/EmptyScreen';
import { IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { EmptyPartySvg } from '@sh4res/bo-common/graphics/EmptyPartySvg';
import { useCanAccess } from '@sh4res/bo-common/hooks';
import { useResourceBasePath } from '@sh4res/bo-common/hooks/useResourceBasePath';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

// The list does not expose the correspondence (nor the email states) of the admins
type AdminsListRow = Omit<GetB2bAdminsResponse[number], 'company_id'> & {
  ghost_link: null | string;
};

const defaultSort: RaSort<GetB2bAdminsQuery> = { field: 'id', order: 'DESC' };

const testId = 'BoExternalB2bAdminsList';

export const B2bAdminsList = ({
  ghostLinkComponent,
}: {
  ghostLinkComponent: React.ReactElement;
}) => {
  const resourceBasePath = useResourceBasePath();
  const canAccessGhostLogin = useCanAccess({
    resource: `${resourceBasePath}admins/login_link`,
    action: 'show',
  });
  const record = useRecordContext<GetB2bCompaniesOneResponse>();
  const adminsListColumns: ColumnList<AdminsListRow> = {
    id: <IdField reference={`${resourceBasePath}admins`} data-testid={`${testId}_IdField`} />,
    role_in_company: <TextField data-testid={`${testId}_RoleInCompanyField`} />,
    email: <TextField data-testid={`${testId}_EmailField`} />,
    status: <SelectChipField choices={adminStatusChoices} data-testid={`${testId}_StatusField`} />,
    role: <SelectChipField choices={adminRoleChoices} data-testid={`${testId}_RoleField`} />,
    firstname: <TextField data-testid={`${testId}_FirstNameField`} />,
    lastname: <TextField data-testid={`${testId}_LastNameField`} />,
    gender: <SelectChipField choices={genderChoices} data-testid={`${testId}_GenderField`} />,
    phone_number: <TextField data-testid={`${testId}_PhoneNumberField`} />,
    secondary_email: <TextField data-testid={`${testId}_SecondaryEmailField`} />,
    created_at: <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_CreatedAt`} />,
    updated_at: <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_UpdatedAt`} />,
    last_invitation_sent_at: (
      <UTCDateField showTime data-testid={`${testId}_LastInvitationSentAt`} />
    ),
    ghost_link: ghostLinkComponent,
    is_migrated: <BooleanField data-testid={`${testId}_IsMigrated`} />,
    email_verified: <BooleanField data-testid={`${testId}_EmailVerified`} />,
    language_preference: (
      <SelectChipField
        choices={languagePreferenceChoices}
        data-testid={`${testId}_LanguagePreferenceField`}
      />
    ),
    is_third_party: <BooleanField data-testid={`${testId}_IsThirdPartyField`} />,
    third_party_legal_structure: (
      <TextField data-testid={`${testId}_ThirdPartyLegalStructureField`} />
    ),
    third_party_mandate_document_id: (
      <TextField data-testid={`${testId}_ThirdPartyMandateDocumentIdField`} />
    ),
  };

  const displayedColumns = useMemo<(keyof AdminsListRow)[]>(
    () => [
      'id',
      'email',
      'status',
      'role',
      'firstname',
      'lastname',
      'phone_number',
      'email_verified',
      'last_invitation_sent_at',
      'created_at',
      'updated_at',
      ...(canAccessGhostLogin ? ['ghost_link' as const] : []),
    ],
    [canAccessGhostLogin],
  );

  if (!record) {
    return null;
  }

  return (
    <ReferenceManyField
      reference={`${resourceBasePath}admins`}
      target="company_id"
      data-testid={`${testId}_ReferenceManyField`}
      sort={defaultSort}>
      <Box sx={{ paddingTop: '3px', paddingBottom: '3px' }}>
        <DatagridTyped<AdminsListRow>
          rowClick="show"
          sortableColumns={getB2bAdminsSortColumns}
          columnRegistry={adminsListColumns}
          displayedColumns={displayedColumns}
          showCard={true}
          bulkActionButtons={
            <BulkSendInvitesButton data-testid={`${testId}_BulkSendInvitesButton`} />
          }
          isRowSelectable={(row) => canResendB2bAdminInvitation(row)}
          empty={
            <EmptyScreen
              svg={<EmptyPartySvg />}
              button={
                <B2bAdminCreate
                  variant="contained"
                  resource={`${resourceBasePath}admins`}
                  label="resources.bo_common.b2b.admins.button"
                  size="medium"
                  icon={<></>}
                  color="primary"
                  sx={{ textTransform: 'none' }}
                  record={{ company_id: record.id }}
                />
              }
              label="resources.bo_common.b2b.admins.empty"
            />
          }
        />
      </Box>
    </ReferenceManyField>
  );
};

const BulkSendInvitesButton = ({ 'data-testid': dataTestId }: { 'data-testid': string }) => {
  const { selectedIds, onUnselectItems, refetch, data } = useListContext<AdminsListRow>();
  const record = useRecordContext<GetB2bCompaniesOneResponse>();

  const adminsMap = useMemo(() => new Map(data.map((item) => [item.id, item])), [data]);

  const disabled = useMemo(() => {
    if (selectedIds.length === 0) {
      return true;
    }
    return selectedIds.some((id) => {
      const admin = adminsMap.get(id);

      return !admin || !canResendB2bAdminInvitation(admin);
    });
  }, [adminsMap, selectedIds]);

  if (!record) {
    return null;
  }

  return (
    <B2bAdminSendInvitesButton
      adminIds={selectedIds}
      companyId={record.id}
      disabled={disabled}
      testId={dataTestId}
      onSuccess={() => {
        onUnselectItems();
        refetch();
      }}
      onError={() => {
        onUnselectItems();
      }}
    />
  );
};
